import { useEffect, useState } from 'react';

const navItems = [
  { label: 'Home', path: '/', icon: 'home' },
  { label: 'Predict', path: '/predictor', icon: 'predictor' },
  { label: 'Colleges', path: '/colleges', icon: 'college' },
  { label: 'Cutoffs', path: '/cutoff-trends', icon: 'matrix' },
  { label: 'Menu', action: 'menu', icon: 'menu' },
];

const BottomIcon = ({ type, className = 'h-5 w-5' }) => {
  switch (type) {
    case 'home':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 11.5 12 4l9 7.5" />
          <path d="M5 10.5V20h14v-9.5" />
          <path d="M9 20v-6h6v6" />
        </svg>
      );
    case 'predictor':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 20V10" />
          <path d="M10 20V4" />
          <path d="M16 20v-7" />
          <path d="M22 20v-11" />
        </svg>
      );
    case 'college':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m3 10 9-5 9 5" />
          <path d="M5 10v9" />
          <path d="M19 10v9" />
          <path d="M3 19h18" />
          <path d="M9 14h6" />
        </svg>
      );
    case 'matrix':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <rect width="18" height="18" x="3" y="3" rx="2" />
          <path d="M3 9h18" />
          <path d="M3 15h18" />
          <path d="M9 3v18" />
          <path d="M15 3v18" />
        </svg>
      );
    case 'menu':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="4" x2="20" y1="12" y2="12" />
          <line x1="4" x2="20" y1="6" y2="6" />
          <line x1="4" x2="20" y1="18" y2="18" />
        </svg>
      );
    default:
      return null;
  }
};

export default function MobileBottomNav({ currentPath, onNavigate, onOpenMenu, isMenuOpen }) {
  const [isVisible, setIsVisible] = useState(true);

  // Hide on virtual keyboard popup (when screen height shrinks noticeably)
  useEffect(() => {
    let initialHeight = window.innerHeight;

    const handleResize = () => {
      if (window.visualViewport) {
        setIsVisible(window.visualViewport.height > initialHeight - 160);
      }
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!isVisible) return null;

  const isActive = (item) => {
    if (item.action === 'menu') return !!isMenuOpen;
    const path = item.path;
    if (path === '/') return currentPath === '/';
    if (path === '/predictor') {
      return currentPath.includes('predictor');
    }
    if (path === '/colleges') {
      return currentPath.startsWith('/colleges') || currentPath.includes('-colleges');
    }
    if (path === '/cutoff-trends') {
      return currentPath.includes('cutoff') || currentPath.includes('matrix');
    }
    return currentPath === path;
  };

  const handleItemClick = (e, item) => {
    if (item.action === 'menu') {
      if (e) e.preventDefault();
      onOpenMenu?.();
      return;
    }
    if (e && (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey)) {
      return;
    }
    if (e) e.preventDefault();
    onNavigate(item.path);
  };

  return (
    <nav
      aria-label="Mobile Bottom Navigation"
      className="fixed bottom-0 inset-x-0 z-30 block lg:hidden border-t border-slate-200/90 bg-white/95 backdrop-blur-md shadow-[0_-4px_20px_rgba(15,23,42,0.08)] dark:border-[#1e355f] dark:bg-[#06142b]/95"
      style={{ paddingBottom: 'max(0px, env(safe-area-inset-bottom))' }}
    >
      <div className="mx-auto flex h-14 max-w-md items-center justify-around px-2">
        {navItems.map((item) => {
          const active = isActive(item);
          const isMenu = item.action === 'menu';
          const Tag = isMenu ? 'button' : 'a';
          const tagProps = isMenu
            ? {
                type: 'button',
                onClick: (e) => handleItemClick(e, item),
                'aria-label': 'Open navigation menu',
                'aria-expanded': !!isMenuOpen,
                'aria-controls': 'mobile-navigation-drawer',
              }
            : {
                href: `#${item.path}`,
                onClick: (e) => handleItemClick(e, item),
              };

          return (
            <Tag
              key={item.label}
              {...tagProps}
              className={`flex flex-1 flex-col items-center justify-center py-1 text-center transition duration-150 focus-visible:outline-none ${
                active
                  ? 'font-bold text-blue-600 dark:text-blue-400'
                  : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
              }`}
            >
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-lg transition ${
                  active ? 'bg-blue-50 dark:bg-blue-950/60' : ''
                }`}
              >
                <BottomIcon type={item.icon} className="h-5 w-5 shrink-0" />
              </span>
              <span className="mt-0.5 text-[10.5px] leading-none tracking-tight">
                {item.label}
              </span>
            </Tag>
          );
        })}
      </div>
    </nav>
  );
}
